/**
 * ExpenseAggregationsPage
 * List and create expense aggregations, scoped to a selected property.
 * Route: /expense-aggregations
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { aggregationsApi, listProperties, type PropertyWithStats } from '../lib/api';
import { PageHeader, EmptyState } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock } from '../components/ui/HelpTooltip';
import type { AggregationListItem } from '../types/expense.types';
import { formatAmount } from '../utils/expense.utils';
import { getDatePresetRange } from '../utils/date-preset.utils';

const propertyTimezone = 'America/New_York';

export function ExpenseAggregationsPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  // Date filter state for the aggregation list
  const last30Range = getDatePresetRange('last30', propertyTimezone);
  const [datePreset, setDatePreset] = useState('last30');
  const [startDate, setStartDate] = useState(last30Range.start);
  const [endDate, setEndDate] = useState(last30Range.end);

  const [aggregations, setAggregations] = useState<AggregationListItem[]>([]);
  const [aggregationsLoading, setAggregationsLoading] = useState(false);
  const [aggregationsError, setAggregationsError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load properties for the create-form selector (no auto-select)
  useEffect(() => {
    listProperties()
      .then((res) => {
        setProperties(res.properties);
      })
      .catch(() => {/* non-fatal */})
      .finally(() => setPropertiesLoading(false));
  }, []);

  // Load aggregations portfolio-wide, filtered by date range
  useEffect(() => {
    setAggregationsLoading(true);
    setAggregationsError(null);
    aggregationsApi
      .list(undefined, startDate, endDate)
      .then((res) => setAggregations(res.aggregations))
      .catch((err) => setAggregationsError(err instanceof Error ? err.message : 'Failed to load aggregations'))
      .finally(() => setAggregationsLoading(false));
  }, [startDate, endDate]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPropertyId || !createName.trim()) return;
    setIsSubmitting(true);
    setCreateError(null);
    try {
      const result = await aggregationsApi.create({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
        propertyId: selectedPropertyId,
      });
      setAggregations((prev) => [result, ...prev]);
      setCreateName('');
      setCreateDescription('');
      setSelectedPropertyId('');
      setIsCreating(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create aggregation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (propertiesLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <Skeleton className="h-10 w-64 mb-2 rounded-lg" />
          <Skeleton className="h-4 w-96 mb-6 rounded-lg" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ExpensesLayout>
    <div data-testid="expense-aggregations-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Expense Aggregations"
          subtitle="Group related expenses for capital improvements, move-outs, and other multi-expense events."
          action={
            <Button
              size="sm"
              onClick={() => { setIsCreating(true); setCreateError(null); }}
              data-testid="aggregation-create-button"
              aria-label="Create new aggregation"
            >
              + New Aggregation
            </Button>
          }
        />
      </div>

      {/* Help block */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <HelpBlock
          summary="Aggregations let you group related expenses together — for example, all costs from a kitchen renovation or a tenant move-out."
          details={"Why use aggregations: When a project involves multiple receipts (materials, labor, permits), grouping them gives you a single total and keeps your records organized. This is especially useful for capital improvements, which may need to be reported differently at tax time.\n\nClassification badges:\n\u2022 A named type (e.g. \"Capital Improvement\") means you've confirmed what kind of project this is.\n\u2022 \"AI: ...\" means the system suggested a classification you haven't confirmed yet.\n\u2022 \"Unclassified\" means no type has been assigned.\n\nYou can add existing confirmed expenses to an aggregation from its detail page."}
        />
      </div>

      {/* Date filter for the aggregation list */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[150px]">
            <label htmlFor="agg-date-preset" className="block text-xs font-medium text-muted-foreground mb-1">
              Date Range
            </label>
            <select
              id="agg-date-preset"
              value={datePreset}
              onChange={(e) => {
                const preset = e.target.value;
                setDatePreset(preset);
                if (preset !== 'custom') {
                  const range = getDatePresetRange(preset, propertyTimezone);
                  setStartDate(range.start);
                  setEndDate(range.end);
                }
              }}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              data-testid="agg-filter-date-preset"
              aria-label="Filter aggregations by date range"
            >
              <option value="last30">Last 30 Days</option>
              <option value="last60">Last 60 Days</option>
              <option value="last90">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {datePreset === 'custom' && (
            <>
              <div className="flex-1 min-w-[130px]">
                <label htmlFor="agg-start-date" className="block text-xs font-medium text-muted-foreground mb-1">From</label>
                <input
                  id="agg-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                  data-testid="agg-filter-start-date"
                  aria-label="Aggregation start date filter"
                />
              </div>
              <div className="flex-1 min-w-[130px]">
                <label htmlFor="agg-end-date" className="block text-xs font-medium text-muted-foreground mb-1">To</label>
                <input
                  id="agg-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                  data-testid="agg-filter-end-date"
                  aria-label="Aggregation end date filter"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create form */}
      {isCreating && (
        <div className="px-[var(--page-padding-x)]">
          <div className="rounded-lg border border-border bg-card p-4 mb-4" data-testid="aggregation-create-form">
            <h2 className="m-0 mb-4 text-base font-semibold text-foreground">
              New Aggregation
            </h2>
            {createError && (
              <p role="alert" className="m-0 mb-3 text-xs text-destructive">
                {createError}
              </p>
            )}
            <form onSubmit={handleCreate} className="flex flex-col gap-3">
              <div>
                <label
                  htmlFor="aggregation-property-select"
                  className="block text-xs font-medium text-foreground mb-1"
                >
                  Property (for new aggregation) *
                </label>
                {properties.length === 0 ? (
                  <p className="m-0 text-sm text-muted-foreground">No properties found.</p>
                ) : (
                  <select
                    id="aggregation-property-select"
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                    data-testid="aggregation-property-select"
                    aria-label="Select property for new aggregation"
                    required
                  >
                    <option value="">-- Select property --</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label
                  htmlFor="aggregation-create-name"
                  className="block text-xs font-medium text-foreground mb-1"
                >
                  Name *
                </label>
                <input
                  id="aggregation-create-name"
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Kitchen Renovation, Move-Out Repairs"
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                  data-testid="aggregation-create-name"
                  aria-label="Aggregation name"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label
                  htmlFor="aggregation-create-description"
                  className="block text-xs font-medium text-foreground mb-1"
                >
                  Description
                </label>
                <input
                  id="aggregation-create-description"
                  type="text"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
                  data-testid="aggregation-create-description"
                  aria-label="Aggregation description"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={isSubmitting || !createName.trim()}
                  data-testid="aggregation-create-submit"
                >
                  {isSubmitting ? 'Creating...' : 'Create Aggregation'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setIsCreating(false); setCreateName(''); setCreateDescription(''); setSelectedPropertyId(''); }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Aggregations list */}
      <div className="px-[var(--page-padding-x)]">
        {aggregationsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : aggregationsError ? (
          <div
            role="alert"
            className="p-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
          >
            {aggregationsError}
          </div>
        ) : aggregations.length === 0 ? (
          <div data-testid="aggregation-empty-state">
            <EmptyState
              title="No aggregations yet"
              description="Create an aggregation to group related expenses for capital improvements, move-outs, or other events."
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="aggregation-list">
            {aggregations.map((agg) => (
              <div
                key={agg.id}
                className="rounded-lg border border-border bg-card flex justify-between items-center gap-4 hover:bg-accent transition-colors"
                data-testid={`aggregation-item-${agg.id}`}
              >
                {/* Left clickable area — navigates to detail */}
                <Link
                  to={`/expense-aggregations/${agg.id}`}
                  className="flex-1 min-w-0 no-underline block p-4"
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">
                      {agg.name}
                    </span>
                    {agg.aggregationType ? (
                      <Badge variant="secondary" className="bg-primary/10 text-primary">{agg.aggregationType}</Badge>
                    ) : agg.aggregationTypeAiSuggested ? (
                      <Badge variant="secondary">
                        AI: {agg.aggregationTypeAiSuggested}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Unclassified</Badge>
                    )}
                  </div>
                  {agg.description && (
                    <p className="m-0 text-xs text-muted-foreground truncate">
                      {agg.description}
                    </p>
                  )}
                  <p className="mt-1 m-0 text-xs text-muted-foreground">
                    {agg.expenseCount} expense{agg.expenseCount !== 1 ? 's' : ''}
                  </p>
                </Link>

                {/* Right: total + Manage button (sibling of Link, not nested inside) */}
                <div className="flex items-center gap-3 shrink-0 pr-4">
                  <p className="m-0 text-xl font-bold text-foreground">
                    {formatAmount(agg.totalAmount)}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/expense-aggregations/${agg.id}`)}
                    data-testid={`aggregation-manage-${agg.id}`}
                    aria-label={`Manage ${agg.name}`}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </ExpensesLayout>
  );
}

export default ExpenseAggregationsPage;
